import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import _ from 'lodash';
import { fetchCompanyFilters, getFilterSellers } from '../../store/actions/rateCard';
import { getAllSellersList, getAllPricingServiceTypes, getPricingSubServiceTypesById } from '../../store/actions/company';
import { getWarehouseListing } from '../../store/actions/inventory.action';
import { getSelectList, computeAutoSelectedSellers } from '../../config/utils';
import PropTypes from 'prop-types';

const PricingTemplateFilter = ({ onFilter, onReset, initialFilters }) => {
    const dispatch = useDispatch();
    const { filterCompanies, filterAllSellers } = useSelector((state) => state.RateCardReducer);
    const { warehouseList } = useSelector((state) => state.InventoryManagement);

    const [companyList, setCompanyList] = useState([]);
    const [sellerList, setSellerList] = useState([]);
    const [allSellers, setAllSellers] = useState([]);
    const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
    const [subServiceTypeOptions, setSubServiceTypeOptions] = useState([]);
    const initialSubServiceFetchedRef = useRef(false);

    const [filters, setFilters] = useState({
        warehouseList: [],
        companyIdList: [],
        sellerIdList: [],
        serviceTypes: [],
        subServiceTypes: [],
        skuList: '',
    });

    const userDetails = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const tenantId = userDetails?.Value?.tenantId || 0;

    const getServiceRequestPayload = (serviceTypeMasterId) => {
        const payload = {
            tenantId,
            searchKeywords: '',
        };
        if (serviceTypeMasterId) {
            payload.serviceTypeMaster_Id = serviceTypeMasterId;
        }
        return payload;
    };

    const mapServiceOptions = (response) => {
        const list = response?.data?.Entity?.ListServicesEntity || [];
        return list.map((item) => ({ value: item.Id, label: item.name }));
    };

    const fetchServiceTypeOptions = async () => {
        const response = await getAllPricingServiceTypes(getServiceRequestPayload());
        if (response?.data?.Status === 200) {
            setServiceTypeOptions(mapServiceOptions(response));
        }
    };

    const fetchSubServiceTypeOptions = async (selectedServices) => {
        const selectedServiceIds = _.map(selectedServices || [], 'value');
        if (!selectedServiceIds.length) {
            setSubServiceTypeOptions([]);
            setFilters((prev) => ({ ...prev, subServiceTypes: [] }));
            return;
        }

        const requests = selectedServiceIds.map((serviceTypeMasterId) =>
            getPricingSubServiceTypesById(getServiceRequestPayload(serviceTypeMasterId))
        );
        const responses = await Promise.all(requests);
        const mergedSubServices = _.uniqBy(
            responses.flatMap((response) => mapServiceOptions(response)),
            'value'
        );
        setSubServiceTypeOptions(mergedSubServices);

        const validSubServiceIdSet = new Set(_.map(mergedSubServices, 'value'));
        setFilters((prev) => ({
            ...prev,
            subServiceTypes: (prev.subServiceTypes || []).filter((item) => validSubServiceIdSet.has(item.value)),
        }));
    };

    useEffect(() => {
        dispatch(fetchCompanyFilters({ pageIndex: 1, pageSize: 10, sortColumn: 'name', sortOrder: 'ascending' }));
        dispatch(getFilterSellers({
            pageIndex: 1, pageSize: 10, sortColumn: 'name', sortOrder: 'ASCENDING',
            companyIdList: [0], screenType: 2,
        }));
        dispatch(getWarehouseListing({ pageIndex: 1, pageSize: 1000 }));
        fetchServiceTypeOptions();
    }, []);

    useEffect(() => {
        if (filterCompanies?.Entity?.listCompanyMasterOrdersEntity?.length) {
            setCompanyList(getSelectList(filterCompanies.Entity.listCompanyMasterOrdersEntity, 'Id', 'name'));
        }
        const sellers = filterAllSellers?.EntityList || [];
        if (sellers.length) {
            const formatted = getSelectList(sellers, 'id', 'sellerName');
            setAllSellers(formatted);
            setSellerList(formatted);
        }
    }, [filterCompanies, filterAllSellers]);

    useEffect(() => {
        initialSubServiceFetchedRef.current = false;
    }, [initialFilters]);

    useEffect(() => {
        if (!initialFilters) {
            return;
        }

        const warehouseOptions = (warehouseList || []).filter((item) => (initialFilters.warehouseList || []).includes(item.value));
        const companyOptions = companyList.filter((item) => (initialFilters.companyIdList || []).includes(item.Id || item.value));
        const sellerOptions = sellerList.filter((item) => (initialFilters.sellerIdList || []).includes(item.id || item.value));
        const serviceOptions = serviceTypeOptions.filter((item) => (initialFilters.serviceTypes || []).includes(item.value));

        setFilters((prev) => ({
            ...prev,
            warehouseList: warehouseOptions,
            companyIdList: companyOptions,
            sellerIdList: sellerOptions,
            serviceTypes: serviceOptions,
            subServiceTypes: [],
            skuList: Array.isArray(initialFilters.skuList) ? initialFilters.skuList.join(',') : '',
        }));

        if (serviceOptions.length && !initialSubServiceFetchedRef.current) {
            initialSubServiceFetchedRef.current = true;
            fetchSubServiceTypeOptions(serviceOptions);
        }
    }, [initialFilters, warehouseList, companyList, sellerList, serviceTypeOptions]);

    useEffect(() => {
        if (!initialFilters || !subServiceTypeOptions.length) {
            return;
        }

        const subServiceOptions = subServiceTypeOptions.filter((item) => (initialFilters.subServiceTypes || []).includes(item.value));
        setFilters((prev) => ({ ...prev, subServiceTypes: subServiceOptions }));
    }, [initialFilters, subServiceTypeOptions]);

    const handleCompanyChange = async (selected) => {
        setFilters((prev) => ({ ...prev, companyIdList: selected || [], sellerIdList: [] }));
        if (selected?.length) {
            const companyIds = _.map(selected, 'Id');
            const res = await getAllSellersList({ companyIds });
            if (res?.data?.Status === 200) {
                const dataList = res.data.Entity.listSellerMasterOrdersEntity.map((item) => ({
                    ...item, label: item.sellerAndCompany, value: item.id,
                }));
                setSellerList(dataList);
                // Auto-select the lone seller when a picked company has exactly one seller
                setFilters((prev) => {
                    const merged = computeAutoSelectedSellers(dataList, companyIds, prev.sellerIdList || []);
                    if (merged.length === (prev.sellerIdList?.length || 0)) return prev;
                    return { ...prev, sellerIdList: merged };
                });
            }
        } else {
            setSellerList(allSellers);
        }
    };

    const handleFilter = () => {
        const validSubServiceValues = new Set(_.map(subServiceTypeOptions, 'value'));
        const selectedSubServices = _.map(filters.subServiceTypes, 'value').filter((value) => validSubServiceValues.has(value));

        onFilter({
            warehouseList: _.map(filters.warehouseList, 'value'),
            companyIdList: _.map(filters.companyIdList, item => item.Id || item.value),
            sellerIdList: _.map(filters.sellerIdList, item => item.id || item.value),
            serviceTypes: _.map(filters.serviceTypes, 'value'),
            subServiceTypes: selectedSubServices,
            skuList: filters.skuList
                ? filters.skuList.split(',').map((sku) => sku.trim()).filter(Boolean)
                : [],
        });
    };

    const handleReset = () => {
        setFilters({ warehouseList: [], companyIdList: [], sellerIdList: [], serviceTypes: [], subServiceTypes: [], skuList: '' });
        setSubServiceTypeOptions([]);
        setSellerList(allSellers);
        onReset();
    };

    return (
        <div className="row mb-3">
            <div className="col-lg-3 col-md-6 col-sm-12 mb-2">
                <label className="labelHead" htmlFor="filter-warehouse">Warehouse</label>
                <Select
                    inputId="filter-warehouse"
                    isMulti
                    closeMenuOnSelect={false}
                    options={warehouseList || []}
                    value={filters.warehouseList}
                    onChange={(val) => setFilters((prev) => ({ ...prev, warehouseList: val || [] }))}
                    className="activeSellerListInput"
                    classNamePrefix="list"
                    placeholder="Select Warehouse(s)"
                />
            </div>
            <div className="col-lg-3 col-md-6 col-sm-12 mb-2">
                <label className="labelHead" htmlFor="filter-company">Company</label>
                <Select
                    inputId="filter-company"
                    isMulti
                    closeMenuOnSelect={false}
                    options={companyList}
                    value={filters.companyIdList}
                    onChange={handleCompanyChange}
                    className="activeSellerListInput"
                    classNamePrefix="list"
                    placeholder="Select Company(s)"
                />
            </div>
            <div className="col-lg-3 col-md-6 col-sm-12 mb-2">
                <label className="labelHead" htmlFor="filter-seller">Seller</label>
                <Select
                    inputId="filter-seller"
                    isMulti
                    options={sellerList}
                    value={filters.sellerIdList}
                    onChange={(val) => setFilters((prev) => ({ ...prev, sellerIdList: val || [] }))}
                    className="activeSellerListInput"
                    classNamePrefix="listSeller"
                    placeholder="Select Seller(s)"
                />
            </div>
            <div className="col-lg-3 col-md-6 col-sm-12 mb-2">
                <label className="labelHead" htmlFor="filter-service-type">Service Type</label>
                <Select
                    inputId="filter-service-type"
                    isMulti
                    closeMenuOnSelect={false}
                    options={serviceTypeOptions}
                    value={filters.serviceTypes}
                    onChange={(val) => {
                        const selected = val || [];
                        setFilters((prev) => ({ ...prev, serviceTypes: selected }));
                        fetchSubServiceTypeOptions(selected);
                    }}
                    className="activeSellerListInput"
                    classNamePrefix="list"
                    placeholder="Select Service Type"
                />
            </div>
            <div className="col-lg-3 col-md-6 col-sm-12 mb-2">
                <label className="labelHead" htmlFor="filter-sub-service-type">Sub Service Type</label>
                <Select
                    inputId="filter-sub-service-type"
                    isMulti
                    closeMenuOnSelect={false}
                    options={subServiceTypeOptions}
                    value={filters.subServiceTypes}
                    onChange={(val) => setFilters((prev) => ({ ...prev, subServiceTypes: val || [] }))}
                    className="activeSellerListInput"
                    classNamePrefix="list"
                    placeholder="Select Sub Service Type"
                />
            </div>
            <div className="col-12 mt-2 d-flex gap-2">
                <button className="btn cus-seconday-bg-btn" onClick={handleFilter}>
                    <i className="fa fa-filter mr-1" aria-hidden="true"></i> Filter
                </button>
                <button className="btn cus-primary-transparent-btn ml-2" onClick={handleReset}>
                    <i className="fa fa-refresh mr-1" aria-hidden="true"></i> Reset
                </button>
            </div>
        </div>
    );
};

PricingTemplateFilter.propTypes = {
    onFilter: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    initialFilters: PropTypes.object,
};

export default PricingTemplateFilter;
